import React from 'react';
import { Box, Typography, ButtonBase } from '@mui/material';
import LocalAtmOutlinedIcon from '@mui/icons-material/LocalAtmOutlined';
import AccountBalanceOutlinedIcon from '@mui/icons-material/AccountBalanceOutlined';
import CreditCardOutlinedIcon from '@mui/icons-material/CreditCardOutlined';
import ReceiptLongOutlinedIcon from '@mui/icons-material/ReceiptLongOutlined';
import QrCode2OutlinedIcon from '@mui/icons-material/QrCode2Outlined';
import AccountBalanceWalletOutlinedIcon from '@mui/icons-material/AccountBalanceWalletOutlined';
import { PaymentMethodType } from '../collectPayment.types';
import { PAYMENT_METHODS } from '../collectPayment.constants';

const PAYMENT_ICONS: Record<PaymentMethodType, React.ReactNode> = {
  cash: <LocalAtmOutlinedIcon sx={{ fontSize: 20 }} />,
  online: <AccountBalanceOutlinedIcon sx={{ fontSize: 20 }} />,
  card: <CreditCardOutlinedIcon sx={{ fontSize: 20 }} />,
  cheque: <ReceiptLongOutlinedIcon sx={{ fontSize: 20 }} />,
  upi: <QrCode2OutlinedIcon sx={{ fontSize: 20 }} />,
  wallet: <AccountBalanceWalletOutlinedIcon sx={{ fontSize: 20 }} />,
};

interface PaymentMethodSelectorProps {
  value: PaymentMethodType;
  onChange: (method: PaymentMethodType) => void;
  disabled?: boolean;
  availableMethods?: PaymentMethodType[];
}

const PaymentMethodSelector: React.FC<PaymentMethodSelectorProps> = ({
  value,
  onChange,
  disabled = false,
  availableMethods,
}) => {
  const methods = availableMethods
    ? PAYMENT_METHODS.filter((m) => availableMethods.includes(m.value))
    : PAYMENT_METHODS;

  return (
    <Box sx={{ mb: 2 }}>
      <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1.5 }}>
        Payment Method
      </Typography>

      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
        {methods.map((method) => {
          const isSelected = value === method.value;
          return (
            <ButtonBase
              key={method.value}
              onClick={() => !disabled && onChange(method.value)}
              disabled={disabled}
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 0.75,
                px: 1.5,
                py: 0.75,
                borderRadius: '20px',
                border: '1.5px solid',
                borderColor: isSelected ? '#d32f2f' : '#e0e0e0',
                backgroundColor: isSelected ? 'rgba(211, 47, 47, 0.08)' : 'transparent',
                color: isSelected ? '#d32f2f' : '#616161',
                transition: 'all 0.2s ease',
                '&:hover': {
                  borderColor: isSelected ? '#d32f2f' : '#bdbdbd',
                  backgroundColor: isSelected ? 'rgba(211, 47, 47, 0.12)' : 'rgba(0, 0, 0, 0.04)',
                },
                '&:disabled': {
                  opacity: 0.5,
                  cursor: 'not-allowed',
                },
              }}
            >
              {PAYMENT_ICONS[method.value]}
              <Typography
                component="span"
                sx={{
                  fontSize: '13px',
                  fontWeight: isSelected ? 600 : 500,
                  whiteSpace: 'nowrap',
                }}
              >
                {method.label}
              </Typography>
            </ButtonBase>
          );
        })}
      </Box>
    </Box>
  );
};

export default PaymentMethodSelector;
